import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2, Calendar, Swords, Building2, FileText } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import PageHeader from '../shared/PageHeader';
import '../shared/shared-chrome.css';
import './Opportunities.css';

/*
 * OpportunitiesPage — /opportunities, the TekStak mirror of the GAS
 * OpportunitiesView (App.jsx ~6032): a Pipeline list, one card per
 * opportunity — name, stage badge, amount, close date, competitor,
 * SFDC opp id — clicking through to the existing
 * /opportunities/:opportunityId detail route.
 *
 * Data: GET /api/opportunities (standalone list; { opportunities, count }).
 * That list omits sf_opportunity_id, so a secondary read of
 * GET /api/workspace/global (whose list_opportunities executor DOES return
 * sf_opportunity_id for the user's open opps) backfills the SFDC id per
 * row. If the backfill read fails the page still works — rows simply show
 * no SFDC id / "Build quote" button, GAS-parity behavior when an opp has
 * no SFDC id.
 */

// Same stage palette as OpportunityDetail.js so list and detail agree.
const STAGE_COLORS = {
  prospecting: 'var(--color-text-muted)',
  qualification: 'var(--color-primary)',
  needs_analysis: 'var(--color-primary)',
  proposal: 'var(--color-warning)',
  negotiation: 'var(--color-warning)',
  closed_won: 'var(--color-success)',
  closed_lost: 'var(--color-error)',
};

function formatCurrency(value) {
  if (value == null || value === '') return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function OpportunitiesPage() {
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['opportunities', 'list'],
    queryFn: () => apiClient.get('/api/opportunities?limit=100'),
    staleTime: 30_000,
  });

  // SFDC-id backfill (see header comment). Non-blocking enrichment.
  const { data: globalData } = useQuery({
    queryKey: ['workspace-global'],
    queryFn: () => apiClient.get('/api/workspace/global'),
    staleTime: 60_000,
  });

  const sfdcById = useMemo(() => {
    const map = new Map();
    (globalData?.opportunities || []).forEach((o) => {
      if (o.id != null && o.sf_opportunity_id) map.set(o.id, o.sf_opportunity_id);
    });
    return map;
  }, [globalData]);

  const opportunities = data?.opportunities || [];
  const openOpps = opportunities.filter((o) => !['closed_won', 'closed_lost'].includes(o.stage));
  const pipelineTotal = openOpps.reduce((sum, o) => sum + (Number(o.value) || 0), 0);

  if (isLoading) {
    return (
      <div className="opps-page">
        <PageHeader title="Opportunities" blurb="Deals, goals, SFDC notes" />
        <div className="opps-state" role="status">
          <Loader2 size={18} className="opps-spin" /> Loading pipeline…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="opps-page">
        <PageHeader title="Opportunities" blurb="Deals, goals, SFDC notes" />
        <div className="opps-state opps-state-error" role="alert">
          <AlertCircle size={18} />
          <span>Pipeline unavailable: {error.message}</span>
          <button type="button" className="opps-retry" onClick={() => refetch()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="opps-page">
      <PageHeader title="Opportunities" blurb="Deals, goals, SFDC notes" />

      <div className="ts-stat-line">
        <span className="ts-stat"><strong>{opportunities.length}</strong> opportunities</span>
        <span className="ts-stat"><strong>{openOpps.length}</strong> open</span>
        <span className="ts-stat">
          <strong>{formatCurrency(pipelineTotal) || '$0'}</strong> open pipeline
        </span>
      </div>

      <h2 className="ts-section-label opps-section-label">Pipeline</h2>

      {opportunities.length === 0 && (
        <div className="ts-card opps-empty">
          No opportunities recorded yet. Create them from an account workspace
          or in Sales AI (&quot;log an opportunity at FTB&hellip;&quot;).
        </div>
      )}

      <div className="opps-list">
        {opportunities.map((o) => {
          const sfdcId = sfdcById.get(o.id) || null;
          const stageLabel = (o.stage || '—').replace(/_/g, ' ');
          const stageColor = STAGE_COLORS[o.stage] || 'var(--color-text-muted)';
          const amount = formatCurrency(o.value);
          const closeDate = formatDate(o.close_date);
          return (
            <div
              key={o.id}
              className="ts-card ts-card--hover opps-row"
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/opportunities/${o.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/opportunities/${o.id}`);
                }
              }}
            >
              <span className="opps-stage" style={{ color: stageColor, borderColor: stageColor }}>
                {stageLabel}
              </span>
              <div className="opps-row-main">
                <div className="opps-row-name">{o.name}</div>
                <div className="opps-row-meta">
                  {amount && <span className="opps-amount">{amount}</span>}
                  {closeDate && (
                    <span className="opps-meta-item">
                      <Calendar size={12} /> close {closeDate}
                    </span>
                  )}
                  {o.competitor && (
                    <span className="opps-meta-item">
                      <Swords size={12} /> vs {o.competitor}
                    </span>
                  )}
                  {o.account_name && (
                    <span className="opps-meta-item">
                      <Building2 size={12} /> {o.account_name}
                    </span>
                  )}
                  {sfdcId && <span className="opps-meta-item opps-sfdc">SFDC {sfdcId}</span>}
                </div>
              </div>
              {sfdcId && (
                <button
                  type="button"
                  className="opps-quote-btn"
                  title="Build a quote for this opportunity"
                  onClick={(e) => { e.stopPropagation(); navigate('/quotes'); }}
                >
                  <FileText size={13} /> Build quote
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
